import { getDocument, setDocument } from './firestore.service'

const NOTIFICATIONS_SETTINGS_COLLECTION = 'notificationsSettings'
const NOTIFICATIONS_SETTINGS_DOC_ID = 'main'

export const getDefaultNotificationsSettings = () => ({
  channels: {
    email: {
      enabled: true,
      provider: 'resend',
      fromName: '',
      fromEmail: '',
      replyTo: '',
      resend: {
        apiKey: ''
      },
      sendpulse: {
        clientId: '',
        clientSecret: ''
      },
      sender: {
        apiToken: '',
        baseUrl: 'https://api.sender.net/v2',
        endpoint: '/transactional/emails'
      }
    },
    whatsapp: {
      enabled: false,
      provider: 'sendmessagecloud',
      sendmessagecloud: {
        senderUid: '',
        senderPhone: ''
      },
      ultramsg: {
        instanceId: '',
        token: ''
      },
      respondio: {
        baseUrl: 'https://api.respond.io/v2',
        apiToken: ''
      }
    }
  },
  internalRecipients: {
    roles: ['admin', 'superadmin'],
    extraEmails: [],
    extraPhones: []
  },
  templates: {
    welcome: {
      customer: {
        email: {
          subject: 'Bienvenido/a a {{storeName}}',
          html: '<p>Hola {{customerName}},</p><p>¡Bienvenido/a a {{storeName}}!</p>'
        },
        whatsapp: {
          message: 'Hola {{customerName}}. ¡Bienvenido/a a {{storeName}}!'
        }
      },
      internal: {
        email: {
          subject: 'Nuevo registro: {{customerName}}',
          html: '<p>Se registró un nuevo cliente: <strong>{{customerName}}</strong>.</p>'
        },
        whatsapp: {
          message: 'Nuevo registro de cliente: {{customerName}}.'
        }
      }
    },
    order_created: {
      customer: {
        email: {
          subject: 'Tu pedido se registró: {{orderNumber}}',
          html: '<p>Hola {{customerName}},</p><p>Tu pedido <strong>{{orderNumber}}</strong> se registró correctamente.</p><p><strong>Entrega:</strong> {{fulfillmentDetails}}</p>{{orderItemsHtml}}<p><strong>Total:</strong> {{orderTotal}}</p>'
        },
        whatsapp: {
          message: 'Hola {{customerName}}. Tu pedido {{orderNumber}} se registró correctamente. Entrega: {{fulfillmentDetails}}. Total: {{orderTotal}}. {{orderItemsText}}'
        }
      },
      internal: {
        email: {
          subject: 'Pedido registrado por {{customerName}}: {{orderNumber}}',
          html: '<p>El cliente <strong>{{customerName}}</strong> registró el pedido <strong>{{orderNumber}}</strong>.</p><p><strong>Contacto:</strong> {{customerEmail}} {{customerPhone}}</p><p><strong>Entrega:</strong> {{fulfillmentDetails}}</p>{{orderItemsHtml}}<p><strong>Total:</strong> {{orderTotal}}</p>'
        },
        whatsapp: {
          message: 'El cliente {{customerName}} registró el pedido {{orderNumber}}. Total: {{orderTotal}}. Entrega: {{fulfillmentDetails}}.'
        }
      }
    },
    payment_approved: {
      customer: {
        email: {
          subject: 'Se confirmó tu pago: {{orderNumber}}',
          html: '<p>Hola {{customerName}},</p><p>Se confirmó el pago de tu compra <strong>{{orderNumber}}</strong>.</p><p><strong>Pagado vía:</strong> {{paidVia}}</p><p><strong>Total:</strong> {{orderTotal}}</p>'
        },
        whatsapp: {
          message: 'Se confirmó tu pago de la compra {{orderNumber}}. Pagado vía: {{paidVia}}. Total: {{orderTotal}}.'
        }
      },
      internal: {
        email: {
          subject: 'Pago registrado de {{customerName}}: {{orderNumber}}',
          html: '<p>El cliente <strong>{{customerName}}</strong> realizó el pago de la compra <strong>{{orderNumber}}</strong>.</p><p><strong>Pagado vía:</strong> {{paidVia}}</p><p><strong>Total:</strong> {{orderTotal}}</p>'
        },
        whatsapp: {
          message: 'El cliente {{customerName}} realizó el pago de la compra {{orderNumber}} vía {{paidVia}} por {{orderTotal}}.'
        }
      }
    },
    order_delivered: {
      customer: {
        email: {
          subject: 'Tu compra fue entregada: {{orderNumber}}',
          html: '<p>Hola {{customerName}},</p><p>Tu compra <strong>{{orderNumber}}</strong> fue entregada.</p><p><strong>Entrega:</strong> {{fulfillmentDetails}}</p><p>{{pdfUrl}}</p><p>¡Gracias por elegirnos!</p>'
        },
        whatsapp: {
          message: 'Tu compra {{orderNumber}} fue entregada. Entrega: {{fulfillmentDetails}}. {{pdfUrl}} ¡Gracias por elegirnos!'
        }
      },
      internal: {
        email: {
          subject: 'Entrega confirmada: {{orderNumber}}',
          html: '<p>Se marcó como entregada la compra <strong>{{orderNumber}}</strong> del cliente <strong>{{customerName}}</strong>.</p><p><strong>Entrega:</strong> {{fulfillmentDetails}}</p>'
        },
        whatsapp: {
          message: 'Entrega confirmada del pedido {{orderNumber}} (cliente {{customerName}}).'
        }
      }
    }
  },
  designOverrides: {},
  updatedAt: new Date()
})

const normalizeString = (value) => (typeof value === 'string' ? value : '')

const normalizeStringList = (value) => {
  if (!Array.isArray(value)) return []
  return value
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean)
}

const normalizeTemplate = (tpl, fallback) => {
  const email = tpl?.email || {}
  const whatsapp = tpl?.whatsapp || {}
  return {
    email: {
      subject: normalizeString(email.subject) || fallback.email.subject,
      html: normalizeString(email.html) || fallback.email.html
    },
    whatsapp: {
      message: normalizeString(whatsapp.message) || fallback.whatsapp.message
    }
  }
}

const normalizeTargetedTemplate = (tpl, fallback) => {
  const legacy = tpl && (tpl.email || tpl.whatsapp) ? tpl : null
  const customerTpl = tpl?.customer || legacy
  const internalTpl = tpl?.internal || null
  return {
    customer: normalizeTemplate(customerTpl, fallback.customer),
    internal: normalizeTemplate(internalTpl, fallback.internal)
  }
}

export const normalizeNotificationsSettings = (settings) => {
  const defaults = getDefaultNotificationsSettings()
  const email = settings?.channels?.email || {}
  const whatsapp = settings?.channels?.whatsapp || {}

  const templates = settings?.templates || {}

  const emailProviderRaw = typeof email.provider === 'string' ? email.provider : ''
  const emailProvider = new Set(['resend', 'sendpulse', 'sender']).has(emailProviderRaw) ? emailProviderRaw : 'resend'
  const resend = email?.resend || {}
  const sendpulse = email?.sendpulse || {}
  const sender = email?.sender || {}

  const whatsappProviderRaw = typeof whatsapp?.provider === 'string' ? whatsapp.provider : ''
  const whatsappProvider = new Set(['sendmessagecloud', 'respondio', 'ultramsg']).has(whatsappProviderRaw)
    ? whatsappProviderRaw
    : 'sendmessagecloud'

  const sendmessagecloud = whatsapp?.sendmessagecloud || {}
  const ultramsg = whatsapp?.ultramsg || {}
  const respondio = whatsapp?.respondio || {}

  const legacySenderUid = normalizeString(whatsapp.senderUid)
  const legacySenderPhone = normalizeString(whatsapp.senderPhone)

  return {
    channels: {
      email: {
        enabled: email.enabled !== false,
        provider: emailProvider,
        fromName: normalizeString(email.fromName),
        fromEmail: normalizeString(email.fromEmail),
        replyTo: normalizeString(email.replyTo),
        resend: {
          apiKey: normalizeString(resend.apiKey)
        },
        sendpulse: {
          clientId: normalizeString(sendpulse.clientId),
          clientSecret: normalizeString(sendpulse.clientSecret)
        },
        sender: {
          apiToken: normalizeString(sender.apiToken),
          baseUrl: normalizeString(sender.baseUrl) || defaults.channels.email.sender.baseUrl,
          endpoint: normalizeString(sender.endpoint) || defaults.channels.email.sender.endpoint
        }
      },
      whatsapp: {
        enabled: whatsapp.enabled === true,
        provider: whatsappProvider,
        sendmessagecloud: {
          senderUid: normalizeString(sendmessagecloud.senderUid) || legacySenderUid,
          senderPhone: normalizeString(sendmessagecloud.senderPhone) || legacySenderPhone
        },
        ultramsg: {
          instanceId: normalizeString(ultramsg.instanceId),
          token: normalizeString(ultramsg.token)
        },
        respondio: {
          baseUrl: normalizeString(respondio.baseUrl) || defaults.channels.whatsapp.respondio.baseUrl,
          apiToken: normalizeString(respondio.apiToken)
        }
      }
    },
    internalRecipients: {
      roles: normalizeStringList(settings?.internalRecipients?.roles) || defaults.internalRecipients.roles,
      extraEmails: normalizeStringList(settings?.internalRecipients?.extraEmails),
      extraPhones: normalizeStringList(settings?.internalRecipients?.extraPhones)
    },
    templates: {
      welcome: normalizeTargetedTemplate(templates.welcome, defaults.templates.welcome),
      order_created: normalizeTargetedTemplate(templates.order_created, defaults.templates.order_created),
      payment_approved: normalizeTargetedTemplate(templates.payment_approved, defaults.templates.payment_approved),
      order_delivered: normalizeTargetedTemplate(templates.order_delivered, defaults.templates.order_delivered)
    },
    designOverrides: settings?.designOverrides && typeof settings.designOverrides === 'object'
      ? settings.designOverrides
      : {},
    updatedAt: settings?.updatedAt || defaults.updatedAt
  }
}

export const getNotificationsSettings = async () => {
  try {
    const settings = await getDocument(NOTIFICATIONS_SETTINGS_COLLECTION, NOTIFICATIONS_SETTINGS_DOC_ID)
    if (!settings) return getDefaultNotificationsSettings()
    return normalizeNotificationsSettings(settings)
  } catch (error) {
    console.error('Error obteniendo configuración de notificaciones:', error)
    return getDefaultNotificationsSettings()
  }
}

export const saveNotificationsSettings = async (settings) => {
  try {
    const normalized = normalizeNotificationsSettings(settings)
    const payload = {
      ...normalized,
      updatedAt: new Date()
    }
    await setDocument(NOTIFICATIONS_SETTINGS_COLLECTION, NOTIFICATIONS_SETTINGS_DOC_ID, payload, { merge: true })
  } catch (error) {
    console.error('Error guardando configuración de notificaciones:', error)
    throw error
  }
}

export default {
  getDefaultNotificationsSettings,
  normalizeNotificationsSettings,
  getNotificationsSettings,
  saveNotificationsSettings
}
